import { createContext, useCallback, useContext, type ReactNode } from "react";
import { notification } from "antd";
import {
  Bell,
  Info,
  Mail,
  X,
} from "lucide-react";
import { WhatsAppIcon } from "@/utils/constants/icons";
import { colors } from "@/design-system/theme/colors";
import "./realtime-notification-toast.css";

export type RealtimeNotificationType =
  | "success"
  | "error"
  | "warning"
  | "info";

export interface RealtimeNotificationToastProps {
  type?: RealtimeNotificationType;
  title: string;
  message?: string;
  topic?: "whatsapp" | "email" | "notification" | "system";
}

export interface RealtimeNotificationInput extends RealtimeNotificationToastProps {
  /** Milliseconds, matching the shared Redux notification contract. */
  duration?: number;
}

const ToastContext = createContext<
  ((notification: RealtimeNotificationInput) => void) | null
>(null);

const topicIcons = {
  email: Mail,
  notification: Bell,
  system: Info,
};

export function RealtimeNotificationToast({
  title,
  message,
  topic = "notification",
}: RealtimeNotificationToastProps) {
  const TopicIcon = topic === "whatsapp" ? WhatsAppIcon : topicIcons[topic];

  return (
    <div className="w-full text-left">
      <div className="flex items-start gap-3 px-5 py-4">
        <span
          className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full border"
          style={{
            backgroundColor: colors.surfaceSofter,
            borderColor: colors.primaryExtraLight,
          }}
        >
          <TopicIcon
            size={topic === "whatsapp" ? 19 : 18}
            color={topic === "whatsapp" ? "#25D366" : "#2563EB"}
          />
        </span>
        <div className="min-w-0 flex-1">
          <div
            className="truncate text-base font-semibold leading-5"
            style={{ color: colors.textDarkest }}
          >
            {title}
          </div>
          {message && (
            <p
              className="mt-1 line-clamp-3 text-sm leading-5"
              style={{ color: colors.textDefault }}
            >
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function RealtimeNotificationToastProvider({ children }: { children: ReactNode }) {
  const [api, contextHolder] = notification.useNotification({
    placement: "topRight",
    maxCount: 4,
    stack: { threshold: 3 },
  });

  const showToast = useCallback(
    ({ duration = 5000, ...toast }: RealtimeNotificationInput) => {
      const durationInSeconds = duration > 0 ? duration / 1000 : 0;
      api.open({
        key: `${toast.topic ?? "notification"}-${toast.title}-${Date.now()}`,
        message: null,
        description: <RealtimeNotificationToast {...toast} />,
        duration: durationInSeconds,
        showProgress: duration > 0,
        pauseOnHover: true,
        placement: "topRight",
        closeIcon: <X size={16} />,
        className: "realtime-notification-notice",
      });
    },
    [api],
  );

  return (
    <ToastContext.Provider value={showToast}>
      {contextHolder}
      {children}
    </ToastContext.Provider>
  );
}

export function useRealtimeNotificationToast() {
  const showToast = useContext(ToastContext);
  if (!showToast) {
    throw new Error(
      "useRealtimeNotificationToast must be used within RealtimeNotificationToastProvider",
    );
  }
  return showToast;
}
